import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { useUserData } from '../../../../hooks'
import { getItemsIds } from '../../../../utils/common'
import { isStaffUser, isUnconfirmed } from '../../../../utils/user'
import { Field } from '../../../Field'
import { FileList, FilePreview, useFileUpload } from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
} from '../../../Form'
import { useFormikFieldValue } from '../../../Formik'
import { Content } from '../../../Modal'
import { useWysiwygEditorFullScreenHeight } from '../../../WysiwygEditor'
import {
  useAutoCompleteAddressFormFields,
  useRequestDraft,
} from '../../Request.hooks'
import {
  REQUEST_FORM_FIELD_NAME_APPLICANT,
  REQUEST_FORM_FIELD_NAME_BUILDING,
  REQUEST_FORM_FIELD_NAME_FILES,
  REQUEST_FORM_FIELD_NAME_FLAT,
} from '../RequestForm.constants'
import { getRequestFormRequiredFields } from '../RequestForm.utils'

import { ApplicantField } from './ApplicantField'
import { AssigneeField } from './AssigneeField'
import { BuildingField } from './BuildingField'
import { CategoryField } from './CategoryField'
import { DeadlineField } from './DeadlineField'
import { DescriptionField } from './DescriptionField'
import { FlatField } from './FlatField'
import { RelatedRequestsField } from './RelatedRequestsField'
import { useRequestFormFieldsValidation } from './RequestFormFields.hooks'
import { TitleField } from './TitleField'

import * as styles from './RequestFormFields.module.css'

function RequestFormFields({
  ContentComponent = Content,
  applicantFieldProps,
  buildingFieldProps,
  categoryFieldProps,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  fieldsVisibility = EMPTY_OBJECT,
  filesProps: { getFiles, onDeleteFile } = EMPTY_OBJECT,
  flatFieldProps,
  notifications,
  submitButtonText,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const [activeField, setActiveField] = useState(null)

  const { dirty, isSubmitting, values } = useFormikContext()

  const { contentRef, contentWrapperRef, handleEditorInit } =
    useWysiwygEditorFullScreenHeight({
      isFullScreen: contentComponentProps?.isFullScreen,
    })

  const [files, setFiles] = useFormikFieldValue(
    REQUEST_FORM_FIELD_NAME_FILES,
    EMPTY_LIST
  )

  const { setDraft } = useRequestDraft()

  useEffect(() => {
    if (dirty) {
      setDraft(values)
    }
  }, [dirty, values])

  const user = useUserData()

  const isStaffUserAcc = isStaffUser(user)

  const isUnconfirmedAcc = isUnconfirmed(user)
  const applicantValue = values[REQUEST_FORM_FIELD_NAME_APPLICANT]

  const {
    showApplicantField = isStaffUserAcc,
    showAssigneeField = isStaffUserAcc,
    showDeadlineField = isStaffUserAcc,
    showRelatedRequestsField = isStaffUserAcc,
  } = fieldsVisibility

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onDeleteFile,
    onSetFiles: setFiles,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  useRequestFormFieldsValidation({
    filesError,
    filesUploading,
    onRemoveNotification,
    onSetNotification,
  })

  // Side effect to set building and flat by applicant
  useAutoCompleteAddressFormFields({
    buildingFieldName: REQUEST_FORM_FIELD_NAME_BUILDING,
    currentUser: user,
    flatFieldName: REQUEST_FORM_FIELD_NAME_FLAT,
    userFieldName: REQUEST_FORM_FIELD_NAME_APPLICANT,
  })

  function handleDeleteFile(id) {
    if (getItemsIds(files)?.includes(id)) {
      handleExcludeFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleDeleteFile}
      />
    )
  }

  function handleCancel() {
    onCancel({
      dirty,
      files,
    })
  }

  function handleClickBody() {
    setActiveField(null)
  }

  function handleClickField(name) {
    setActiveField(name)
  }

  const showFiles =
    files?.length > 0 || filesUploading?.length > 0 || filesError?.length > 0

  // Used only for previous `FormField` generation
  const commonFieldProps = {
    activeField,
    onClick: handleClickField,
  }

  const requiredFields = getRequestFormRequiredFields({
    applicant: applicantValue,
    isStaffUser: isStaffUserAcc,
    isUnconfirmed: isUnconfirmedAcc,
  })

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        isSubmitting,
        submitProps: {
          isLoading: isSubmitting,
          title: submitButtonText,
        },
        uploadProps: {
          getFiles,
          onFinishUpload: handleUploadFile,
          onStartUpload: handleStartUploading,
          onUploadErrors: handleUploadErrors,
        },
      }}
      onClick={handleClickBody}
    >
      <ContentComponent
        {...contentComponentProps}
        contentRef={contentRef}
        contentWrapperRef={contentWrapperRef}
        size={contentSize}
      >
        <FormInlineNotifications notifications={notifications} />

        <FormFieldsContainer className={styles.formFields}>
          <TitleField requiredFields={requiredFields} />
          <CategoryField {...categoryFieldProps} />
          <DescriptionField
            {...commonFieldProps}
            requiredFields={requiredFields}
            onInit={handleEditorInit}
          />
          {showApplicantField ? (
            <ApplicantField {...applicantFieldProps} />
          ) : null}
          {!isUnconfirmedAcc ? (
            <>
              <BuildingField
                {...buildingFieldProps}
                requiredFields={requiredFields}
              />
              <FlatField {...flatFieldProps} requiredFields={requiredFields} />
            </>
          ) : null}
          {showAssigneeField ? <AssigneeField /> : null}
          {showDeadlineField ? <DeadlineField {...commonFieldProps} /> : null}
          {showRelatedRequestsField ? <RelatedRequestsField /> : null}
          {showFiles ? (
            <Field
              shouldAdaptToMobileScreen
              disabled={isSubmitting}
              label={`${t('Request.AttachLabelKey')}:`}
            >
              <FileList
                errorItems={filesError}
                files={files}
                renderItem={renderFile}
                uploadingItems={filesUploading}
                onRemoveFile={handleRemoveFile}
              />
            </Field>
          ) : null}
          <FilesPreviewGallery
            files={files}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_REQUEST}
            onClose={handleCloseFilePreview}
          />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default RequestFormFields
